import mila from 'markdown-it-link-attributes';
import mentionPlugin from './markdownIt/link';
import MarkdownIt from 'markdown-it';

const setImageHeight = inlineToken => {
  const imgSrc = inlineToken.attrGet('src');
  if (!imgSrc) return;
  const url = new URL(imgSrc);
  const height = url.searchParams.get('cw_image_height');
  if (!height) return;
  inlineToken.attrSet('style', `height: ${height};`);
};

const processInlineToken = blockToken => {
  blockToken.children.forEach(inlineToken => {
    if (inlineToken.type === 'image') {
      setImageHeight(inlineToken);
    }
  });
};

const imgResizeManager = md => {
  // Custom rule for image resize in markdown
  // If the image url has a query param cw_image_height, then add a style attribute to the image
  md.core.ruler.after('inline', 'add-image-height', state => {
    state.tokens.forEach(blockToken => {
      if (blockToken.type === 'inline') {
        processInlineToken(blockToken);
      }
    });
  });
};

const createMarkdownInstance = (linkify = true) => {
  return MarkdownIt({
    html: true,
    xhtmlOut: true,
    breaks: true,
    langPrefix: 'language-',
    linkify,
    typographer: true,
    quotes: '\u201c\u201d\u2018\u2019',
    maxNesting: 20,
  })
    .use(mentionPlugin)
    .use(imgResizeManager)
    .use(mila, {
      attrs: {
        class: 'link',
        rel: 'noreferrer noopener nofollow',
        target: '_blank',
      },
    });
};

const TWITTER_USERNAME_REGEX = /(^|[^@\w])@(\w{1,15})\b/g;
const TWITTER_USERNAME_REPLACEMENT = '$1[@$2](http://twitter.com/$2)';
const TWITTER_HASH_REGEX = /(^|\s)#(\w+)/g;
const TWITTER_HASH_REPLACEMENT = '$1[#$2](https://twitter.com/hashtag/$2)';

class MessageFormatter {
  // Audio file extensions to detect
  static AUDIO_EXTENSIONS = ['mp3', 'mp4', 'wav', 'ogg', 'm4a', 'flac', 'webm', 'aac'];

  constructor(
    message,
    isATweet = false,
    isAPrivateNote = false,
    linkify = true
  ) {
    this.message = message || '';
    this.isAPrivateNote = isAPrivateNote;
    this.isATweet = isATweet;
    this.linkify = linkify;
    this.md = createMarkdownInstance(linkify);
  }

  isAudioFile(url) {
    if (!url) return false;
    // Remove query parameters
    const path = url.split('?')[0].toLowerCase();
    // Check if URL ends with audio extension
    return MessageFormatter.AUDIO_EXTENSIONS.some(ext => path.endsWith(`.${ext}`));
  }

  convertAudioImagesToAudioTags(html) {
    // Convert <img> tags with audio file URLs to <audio> tags
    // Matches img tags that either have alt="audio" or have audio file URLs
    const audioRegex = /<img[^>]*>/g;
    
    return html.replace(audioRegex, (match) => {
      const srcMatch = match.match(/src="([^"]*)"/);
      const altMatch = match.match(/alt="([^"]*)"/i);
      
      // Check if it's an audio file by URL or by alt text
      if (srcMatch && srcMatch[1]) {
        const src = srcMatch[1];
        const isAudioByUrl = this.isAudioFile(src);
        const isAudioByAlt = altMatch && altMatch[1].toLowerCase() === 'audio';
        
        if (isAudioByUrl || isAudioByAlt) {
          return `<audio controls class="audio-player"><source src="${src}" />Your browser does not support the audio element.</audio>`;
        }
      }
      return match;
    });
  }

  formatMessage() {
    let updatedMessage = this.message;
    if (this.isATweet && !this.isAPrivateNote) {
      updatedMessage = updatedMessage.replace(
        TWITTER_USERNAME_REGEX,
        TWITTER_USERNAME_REPLACEMENT
      );
      updatedMessage = updatedMessage.replace(
        TWITTER_HASH_REGEX,
        TWITTER_HASH_REPLACEMENT
      );
    }
    let html = this.md.render(updatedMessage);
    // Convert any audio image tags to audio tags
    html = this.convertAudioImagesToAudioTags(html);
    return html;
  }

  get formattedMessage() {
    return this.formatMessage();
  }

  get plainText() {
    const strippedOutHtml = new DOMParser().parseFromString(
      this.formattedMessage,
      'text/html'
    );
    return strippedOutHtml.body.textContent || '';
  }
}

export default MessageFormatter;
